import { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    useBillAccrualMatches,
    useBillImportedSettlements,
} from "@api/bill.api";
import {
    useInvoiceAccrualMatches,
    useInvoiceImportedSettlements,
} from "@api/invoice.api";
import { undoAccrualReview } from "@api/accrual-review.api";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { BillSource } from "@common/types/bill";
import { InvoiceSource } from "@common/types/invoice";
import { MatchedTransactionRowData } from "@components/transactions/MatchedTransactionCard/MatchedTransactionCard";
import { invalidateAccrualMatchCaches } from "@components/accounting/accrualReview/accrualReviewCache";
import { useOverlays } from "contexts/OverlayContext";
import { useScope } from "@state/scopeContext.lib";
import {
    getDetachTransactionIds,
    groupMatchesBySplit,
} from "./groupMatchesBySplit";

export type AccrualMatchDocument =
    | { documentType: "bill"; documentId: string; source: BillSource }
    | { documentType: "invoice"; documentId: string; source: InvoiceSource };

export const useAccrualMatchData = (props: AccrualMatchDocument) => {
    const { documentType, documentId } = props;
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const { openOverlay } = useOverlays();

    const billQuery = useBillAccrualMatches(
        documentType === "bill" ? documentId : undefined,
    );
    const invoiceQuery = useInvoiceAccrualMatches(
        documentType === "invoice" ? documentId : undefined,
    );
    const billSettlementsQuery = useBillImportedSettlements({
        billId: props.documentType === "bill" ? documentId : undefined,
        source: props.documentType === "bill" ? props.source : undefined,
    });
    const invoiceSettlementsQuery = useInvoiceImportedSettlements({
        invoiceId: props.documentType === "invoice" ? documentId : undefined,
        source: props.documentType === "invoice" ? props.source : undefined,
    });

    const matchesQuery = documentType === "bill" ? billQuery : invoiceQuery;
    const settlementsQuery =
        documentType === "bill"
            ? billSettlementsQuery
            : invoiceSettlementsQuery;
    const matches = matchesQuery.data;
    const settlements = settlementsQuery.data ?? [];

    // A failed fetch leaves `data` undefined, which would otherwise fall
    // through to the empty branch and state that the document has no matches.
    const failedQueries = [matchesQuery, settlementsQuery].filter(
        (query) => query.isError,
    );
    const hasError = failedQueries.length > 0;
    const isRetrying = failedQueries.some((query) => query.isFetching);
    const isLoading = matchesQuery.isPending || settlementsQuery.isLoading;

    const retry = () => {
        failedQueries.forEach((query) => {
            void query.refetch();
        });
    };

    const handleOpenMatchDrawer = useCallback(
        (transactionId: number) => {
            openOverlay("transactionMatch", { transactionId });
        },
        [openOverlay],
    );

    // Per-transaction concurrency guard — prevents double-click or rapid
    // re-click from firing a second undoAccrualReview for the same row
    // before the first one completes (the second would fail server-side
    // with a confusing error toast).
    const detachingTxIdsRef = useRef<Set<number>>(new Set());

    const handleDetach = useCallback(
        async (transactionId: number) => {
            if (!workspaceId) {
                return;
            }
            if (detachingTxIdsRef.current.has(transactionId)) {
                return;
            }
            detachingTxIdsRef.current.add(transactionId);
            try {
                await undoAccrualReview({ workspaceId, transactionId });
                invalidateAccrualMatchCaches(queryClient, { transactionId });
            } catch {
                // error toast handled by axios interceptor; leave caches
                // as-is so the row stays visible for the user to retry
            } finally {
                detachingTxIdsRef.current.delete(transactionId);
            }
        },
        [workspaceId, queryClient],
    );

    const handleOpenReverseDrawer = useCallback(() => {
        openOverlay("documentAccrualMatch", { documentType, documentId });
    }, [openOverlay, documentType, documentId]);

    const rows = useMemo<MatchedTransactionRowData[]>(
        () =>
            groupMatchesBySplit(matches ?? []).map(
                ({ key, matches: groupMatches }) => {
                    const [first] = groupMatches;
                    const isSplitGroup = groupMatches.length > 1;
                    return {
                        key,
                        date: first.date,
                        dateOnly: first.dateOnly,
                        amount: sumMonetaryValues(
                            groupMatches.map((match) => match.amount),
                        ),
                        bankDescription: first.bankDescription,
                        counterparty: first.counterparty,
                        entity: first.entity,
                        onClick: isSplitGroup
                            ? handleOpenReverseDrawer
                            : () => handleOpenMatchDrawer(first.transactionId),
                        onDetach: async () => {
                            for (const transactionId of getDetachTransactionIds(
                                groupMatches,
                            )) {
                                await handleDetach(transactionId);
                            }
                        },
                    };
                },
            ),
        [matches, handleOpenMatchDrawer, handleOpenReverseDrawer, handleDetach],
    );

    return {
        workspaceId,
        hasError,
        isRetrying,
        isLoading,
        retry,
        settlements,
        rows,
        handleOpenReverseDrawer,
    };
};
